import React, { useEffect, useMemo, useState } from 'react';
import type { PostTag } from '../../types/post';
import { TagType } from '../../types/post';
import { FaTag, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { formatTagDateTime } from '../../utils/dateUtils';
import {
  getPostsOverviewCityLineFromCoordinates,
  postsOverviewCityLineFromStoredName,
} from '../../utils/locationTagSummary';

/** API may send enum as string; normalize for comparisons and class names. */
function normalizeTagTypeValue(tagType: PostTag['tagType'] | string | undefined): string {
  return String(tagType ?? '')
    .trim()
    .toUpperCase();
}

/** Location and time first; general / other tags after (same pill styling). */
function getTagDisplaySortOrder(normalizedType: string): number {
  switch (normalizedType) {
    case TagType.LOCATION:
      return 0;
    case TagType.DATE_TIME:
      return 1;
    default:
      return 2;
  }
}

function sortTagsForDisplay(tags: PostTag[]): PostTag[] {
  return [...tags]
    .map((tag, index) => ({ tag, index }))
    .sort((a, b) => {
      const oa = getTagDisplaySortOrder(normalizeTagTypeValue(a.tag.tagType));
      const ob = getTagDisplaySortOrder(normalizeTagTypeValue(b.tag.tagType));
      if (oa !== ob) return oa - ob;
      return a.index - b.index;
    })
    .map(({ tag }) => tag);
}

function parseCoord(v: number | string | undefined): number | undefined {
  if (v === undefined || v === null) return undefined;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : undefined;
}

/** Post list card: Stadt + Landesflagge; Rohdaten über fullLabel (Tooltip/ARIA). */
const LocationPostsOverviewTag: React.FC<{ tag: PostTag; fullLabel: string }> = ({
  tag,
  fullLabel,
}) => {
  const lat = parseCoord(tag.locationLatitude);
  const lon = parseCoord(tag.locationLongitude);
  const named = tag.locationName?.trim();
  const coordFallback =
    lat != null && lon != null ? `${lat.toFixed(4)}, ${lon.toFixed(4)}` : tag.tagValue || '';
  const [geoLine, setGeoLine] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (lat == null || lon == null) return;
    let cancelled = false;
    void getPostsOverviewCityLineFromCoordinates(lat, lon).then((line) => {
      if (!cancelled && line) setGeoLine(line);
    });
    return () => {
      cancelled = true;
    };
  }, [lat, lon]);

  const fromName = named ? postsOverviewCityLineFromStoredName(named) : undefined;
  const shortLine = geoLine ?? fromName ?? coordFallback;

  return (
    <span className="tag tag--location" title={fullLabel} aria-label={fullLabel}>
      <FaMapMarkerAlt className="tag__icon" aria-hidden />
      <span className="tag__value">{shortLine}</span>
    </span>
  );
};

interface TagDisplayProps {
  tags: PostTag[];
  /**
   * When true (e.g. PostCard): location tag shows municipality + flag only; datetime stays full.
   * Raw location string remains in tooltip/aria-label.
   */
  compact?: boolean;
}

/**
 * Displays a list of post tags with appropriate icons.
 */
export const TagDisplay: React.FC<TagDisplayProps> = ({ tags, compact = false }) => {
  if (!tags || tags.length === 0) {
    return null;
  }

  const getTagIcon = (normalizedType: string) => {
    switch (normalizedType) {
      case TagType.GENERAL:
        return <FaTag />;
      case TagType.LOCATION:
        return <FaMapMarkerAlt />;
      case TagType.DATE_TIME:
        return <FaCalendarAlt />;
      default:
        return <FaTag />;
    }
  };

  const formatTagValue = (tag: PostTag, normalizedType: string): string => {
    switch (normalizedType) {
      case TagType.GENERAL:
        return tag.tagValue || '';
      case TagType.LOCATION:
        return tag.locationName || `${tag.locationLatitude?.toFixed(4)}, ${tag.locationLongitude?.toFixed(4)}`;
      case TagType.DATE_TIME:
        return tag.dateTime ? formatTagDateTime(tag.dateTime) : '';
      default:
        return tag.tagValue || '';
    }
  };

  const rootClass = compact ? 'tag-display tag-display--compact-mobile' : 'tag-display';

  const sortedTags = useMemo(() => sortTagsForDisplay(tags), [tags]);

  return (
    <div className={rootClass}>
      {sortedTags.map((tag) => {
        const normalizedType = normalizeTagTypeValue(tag.tagType);
        const fullLabel = formatTagValue(tag, normalizedType);
        const typeClass = `tag tag--${normalizedType.toLowerCase()}`;

        if (compact && normalizedType === TagType.LOCATION) {
          return <LocationPostsOverviewTag key={tag.id} tag={tag} fullLabel={fullLabel} />;
        }

        if (compact && normalizedType === TagType.DATE_TIME) {
          return (
            <span key={tag.id} className={typeClass} title={fullLabel} aria-label={fullLabel || undefined}>
              <FaCalendarAlt className="tag__icon" aria-hidden />
              <span className="tag__value">{fullLabel}</span>
            </span>
          );
        }

        return (
          <span key={tag.id} className={typeClass}>
            {getTagIcon(normalizedType)}
            <span className="tag__value">{fullLabel}</span>
          </span>
        );
      })}
    </div>
  );
};

